<template>
	<div
		v-if="isPlaybackSpeedEnabled || isQualitySettingsEnabled"
		ref="settingsMenuControl"
		class="player-controls__menu-control"
		@focusout="handleFocusOut"
	>
		<button
			ref="settingsMenuButton"
			v-tooltip="tooltip"
			type="button"
			class="player-controls__button"
			:aria-label="$I18n.trans('commons.settings')"
			aria-haspopup="dialog"
			:aria-controls="menuId"
			:aria-expanded="showMenu ? 'true' : 'false'"
			@click.stop="handleClick"
			@keydown="handleTriggerKeyDown"
			@keyup="handleTriggerKeyUp"
		>
			<Icon
				icon-name="settings-gear"
				:size="controlSize"
			/>
		</button>
		<div
			:id="menuId"
			ref="settingsMenu"
			class="player-controls__menu -settings"
			:class="{ 'is-visible': showMenu }"
			role="dialog"
			:aria-label="$I18n.trans('commons.settings')"
			@keydown.esc.stop.prevent="handleClose"
		>
			<PlayerControlsSettingsMenu
				v-if="showMenu"
				ref="playerControlsSettingsMenu"
				:size="controlSize"
				:menu-label="$I18n.trans('commons.settings')"
				v-bind="menuProps"
				@set-quality="handleSetQuality"
				@set-speed="handleSetSpeed"
				@close="handleClose"
			/>
		</div>
	</div>
</template>

<script>
import Icon from '@shared/components/Icon.vue';
import PlayerControlsSettingsMenu from './PlayerControlsSettingsMenu.vue';
import { VTooltip } from 'v-tooltip';

export default {
	name:       'SettingsButton',
	components: {
		Icon,
		PlayerControlsSettingsMenu,
	},
	directives: {
		tooltip: VTooltip,
	},
	props: {
		isPlaybackSpeedEnabled: {
			type:    Boolean,
			default: false,
		},
		isQualitySettingsEnabled: {
			type:    Boolean,
			default: false,
		},
		controlSize: {
			type:     String,
			required: true,
		},
		isMobileWebApp: {
			type:    Boolean,
			default: false,
		},
		isTouchDevice: {
			type:    Boolean,
			default: false,
		},
		qualityLevels: {
			type:    Array,
			default: () => [],
		},
		currentQualityId: {
			type:    Number,
			default: 0,
		},
		speedOptions: {
			type:     Array,
			required: true,
		},
		currentSpeed: {
			type:    Number,
			default: 1,
		},
	},
	data() {
		return {
			showMenu: false,
			menuId:   `player-settings-menu-${this._uid}`,
		};
	},
	computed: {
		menuProps() {
			const currentSpeed = this.speedOptions.find((speed) => speed.value === this.currentSpeed);

			return {
				variant:                  this.isMobileWebApp ? 'mobile' : 'default',
				qualityOptions:           this.qualityLevels,
				currentQualityId:         this.currentQualityId,
				speedOptions:             this.speedOptions,
				currentSpeedId:           currentSpeed?.id,
				isPlaybackSpeedEnabled:   this.isPlaybackSpeedEnabled,
				isQualitySettingsEnabled: this.isQualitySettingsEnabled,
			};
		},
		tooltip() {
			if (this.isTouchDevice || this.showMenu) return {};

			return {
				content: this.$I18n.trans('commons.settings'),
				classes: `-size-${this.controlSize} tooltip`,
				trigger: 'hover',
			};
		},
	},
	methods: {
		handleTriggerKeyDown(event) {
			if (event.key === 'Escape' && this.showMenu) {
				event.preventDefault();
				event.stopPropagation();
				this.handleClose();
				return;
			}
			if (!['Enter', ' '].includes(event.key)) return;
			event.preventDefault();
			event.stopPropagation();
			this.handleClick();
		},
		handleTriggerKeyUp(event) {
			if (!this.showMenu || !['Enter', ' '].includes(event.key)) return;
			event.preventDefault();
			event.stopPropagation();
			this.focusMenuAfterToggle();
		},
		handleClick() {
			this.showMenu = !this.showMenu;
			this.$emit('menu-toggle', this.showMenu);

			if (this.showMenu) {
				this.$emit('opened');
				this.focusMenuAfterToggle();

				const interval = setInterval(() => {
					if (this.showMenu) {
						this.$emit('still-open');
					} else {
						clearInterval(interval);
					}
				}, 2000);
			}

			if (this.$refs.playerControlsSettingsMenu) {
				this.$refs.playerControlsSettingsMenu.resetToMainMenu();
			}
		},
		handleClose(returnFocus = true) {
			this.showMenu = false;
			this.$emit('menu-toggle', false);
			this.$emit('close');
			if (returnFocus) this.$nextTick(() => this.$refs.settingsMenuButton?.focus());
		},
		handleSetSpeed(id) {
			const speed = this.speedOptions.find((v) => v.id === id);
			this.$emit('set-speed', speed.value);
		},
		handleSetQuality(id) {
			this.$emit('set-quality', id);
		},
		handleFocusOut() {
			setTimeout(() => {
				if (this.showMenu && !this.$refs.settingsMenuControl?.contains(document.activeElement)) this.closeMenu(false);
			}, 0);
		},
		closeMenu(returnFocus = true) {
			if (this.showMenu) {
				this.handleClose(returnFocus);
			}
		},
		// Wait until native button activation is complete so it cannot reclaim focus from the popup.
		focusMenuAfterToggle() {
			this.$nextTick(() => {
				setTimeout(() => this.$refs.settingsMenu?.querySelector('button:not(:disabled)')?.focus(), 0);
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.player-controls__menu-control {
	position: relative;
	display: flex;
}

.player-controls__button {
	display: flex;
	color: var(--color-white);
	line-height: 1;
	cursor: pointer;

	:deep(.player-controls.-dark) & {
		color: var(--color-black);
	}
}

.player-controls__menu {
	position: absolute;
	display: flex;
	flex-direction: column;
	visibility: hidden;
	opacity: 0;
	transition: visibility 0.2s, opacity 0.2s ease-in-out;
	text-align: center;
	bottom: 32px;
	right: -16px;

	&.is-visible {
		visibility: visible;
		opacity: 1;
	}
}
</style>
